import { MoreHorizontal, Plus, Share } from "lucide-react";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import type { PwaPlatform } from "@/lib/pwa-install";

/**
 * "How to install" for browsers with no one-tap install: the steps for the
 * caller's `platform`. Shared by the Settings Install App section and the
 * install banner. See [[install-instructions-dialog.spec]].
 */
export function InstallInstructionsDialog({
  open,
  onOpenChange,
  platform,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  platform: PwaPlatform;
}) {
  const showIosInstructions = platform === "ios-safari";
  const showChromiumInstructions = platform === "chromium";
  const showIosOtherMessage = platform === "ios-other";
  const showDesktopSafari = platform === "desktop-safari";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogTitle className="sr-only">Install Otus</DialogTitle>
        <div className="flex items-center gap-3">
          <img
            src="/icon-192x192.png"
            width={48}
            height={48}
            className="rounded-xl shrink-0"
            alt="Otus"
          />
          <div>
            <p className="text-sm font-semibold text-foreground">
              Install Otus
            </p>
            <p className="text-xs text-muted-foreground">
              Get quick access from your home screen
            </p>
          </div>
        </div>

        {showIosInstructions && (
          <div className="mt-3 space-y-2">
            <div className="flex items-center gap-2.5">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground">
                1
              </span>
              <p className="text-xs text-muted-foreground">
                Tap the{" "}
                <MoreHorizontal className="inline h-3.5 w-3.5 align-text-bottom text-blue-500" />{" "}
                <span className="font-medium text-foreground">ellipsis</span>{" "}
                button in the toolbar
              </p>
            </div>
            <div className="flex items-center gap-2.5">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground">
                2
              </span>
              <p className="text-xs text-muted-foreground">
                Tap{" "}
                <Share className="inline h-3.5 w-3.5 align-text-bottom text-blue-500" />{" "}
                <span className="font-medium text-foreground">Share</span>
              </p>
            </div>
            <div className="flex items-center gap-2.5">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground">
                3
              </span>
              <p className="text-xs text-muted-foreground">
                Scroll and tap{" "}
                <Plus className="inline h-3.5 w-3.5 align-text-bottom text-blue-500" />{" "}
                <span className="font-medium text-foreground">
                  Add to Home Screen
                </span>
              </p>
            </div>
          </div>
        )}

        {showChromiumInstructions && (
          <div className="mt-3 space-y-2">
            <div className="flex items-center gap-2.5">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground">
                1
              </span>
              <p className="text-xs text-muted-foreground">
                Tap the{" "}
                <span className="font-medium text-foreground">&#x22EE;</span>{" "}
                menu in Chrome
              </p>
            </div>
            <div className="flex items-center gap-2.5">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground">
                2
              </span>
              <p className="text-xs text-muted-foreground">
                Tap{" "}
                <span className="font-medium text-foreground">
                  Add to Home Screen
                </span>
              </p>
            </div>
            <div className="flex items-center gap-2.5">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground">
                3
              </span>
              <p className="text-xs text-muted-foreground">
                Tap <span className="font-medium text-foreground">Add</span>
              </p>
            </div>
          </div>
        )}

        {showIosOtherMessage && (
          <p className="mt-3 text-xs text-muted-foreground">
            To install Otus, open this page in{" "}
            <span className="font-medium text-foreground">Safari</span> and use
            the Share menu.
          </p>
        )}

        {showDesktopSafari && (
          <p className="mt-3 text-xs text-muted-foreground">
            To install Otus, go to{" "}
            <span className="font-medium text-foreground">
              File &rarr; Add to Dock
            </span>{" "}
            in Safari.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
